import * as React from "react";
import { useSelector } from "react-redux";
import {
  DashboardMenuItem,
  MenuItemLink,
  useGetIdentity,
  usePermissions,
  useResourceDefinitions,
} from "react-admin";

import DashboardIcon from "@mui/icons-material/Home";
import CategoryIcon from "@mui/icons-material/Category";
import ComposeIcon from "@mui/icons-material/Schema";
import DeveloperIcon from "@mui/icons-material/DeveloperMode";
import CatalogIcon from "@mui/icons-material/LibraryBooks";
import ConfigIcon from "@mui/icons-material/Settings";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CableIcon from "@mui/icons-material/Cable";
import SecurityIcon from "@mui/icons-material/Security";
import SchemaIcon from "@mui/icons-material/Schema";
import DataIcon from "@mui/icons-material/Storage";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useLocation, useNavigate } from "react-router";
import { useEffect, useState } from "react";
import AppExtensionProviderRegistry, {
  AppExtension,
} from "../../framework/app/app.extension.provider";
import {
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from "@mui/material";
import { useDynamicEntities } from "../../hooks/useDynamicEntities";
import { useDataModelHierarchy } from "../../hooks/useDataModelHierarchy";
import { getConfig } from "../../framework/config/app.config";

interface MMAdminMenuProps {
  onMenuClick?: () => void;
  logout?: () => void;
  hideWelcome?: boolean;
}

const MMAdminMenu = ({ onMenuClick, logout, hideWelcome }: MMAdminMenuProps) => {
  const excludedResources = [
    "theme",
    "environment",
    "dashboard",
    "category",
    "item",
  ];

  const resources = useResourceDefinitions();
  const allResourcecs = Object.keys(resources);
  const tenant = sessionStorage.getItem("current-tenant");
  const brand = sessionStorage.getItem("current-brand");
  const navigate = useNavigate();
  const location = useLocation();
  const appExtension: AppExtension =
    AppExtensionProviderRegistry.getDefaultProvider()?.getAppExtension();
  const defaultRoute = appExtension.getDefaultRoute();
  const [dataOpen, setDataOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  
  // Load dynamic entities and data models
  const { entities: dynamicEntities, loading: entitiesLoading } = useDynamicEntities();
  const { domains, loading: domainsLoading } = useDataModelHierarchy();
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());

  const handleDataClick = () => {
    navigate("/data");
    setDataOpen(!dataOpen);
  };

  const handleAIClick = () => {
    navigate("/ai");
    setAiOpen(!aiOpen);
  };

  const toggleDomainSection = (domainId: string) => {
    const newExpanded = new Set(expandedDomains);
    if (newExpanded.has(domainId)) {
      newExpanded.delete(domainId);
    } else {
      newExpanded.add(domainId);
    }
    setExpandedDomains(newExpanded);
  };

  const ResourceSubMenuItem = ({ resourceName, onClick, padding = 16 }) => {
    const resources = useResourceDefinitions();
    const resource = resources[resourceName];

    if (!resource) {
      return null; // If the resource doesn't exist, don't render anything
    }

    return (
      <SubMenuItem
        to={`/${resourceName}`}
        label={resource?.options?.label || resourceName}
        Icon={resource?.icon} // Pass undefined if no icon (avoids React.Fragment warning)
        onClick={onClick}
        padding={padding}
      />
    );
  };

  const SubMenuItem = ({
    to,
    label,
    Icon,
    onClick,
    padding = 32,
    fontSize = "0.875rem",
  }) => {
    // Don't render Icon component if it's React.Fragment (causes warnings)
    const iconElement = Icon && Icon !== React.Fragment 
      ? <Icon style={{ fontSize: "18px", minWidth: "24px" }} /> 
      : undefined;
    
    return (
      <MenuItemLink
        to={to}
        primaryText={label}
        leftIcon={iconElement}
        onClick={onClick}
        style={{
          paddingLeft: `${padding}px`,
          fontSize: fontSize,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minHeight: "36px",
          letterSpacing: "normal",
          wordSpacing: "normal",
        }}
        sx={{
          '& .MuiListItemIcon-root': {
            minWidth: '36px',
          },
          '& .MuiListItemText-primary': {
            fontSize: fontSize,
            letterSpacing: 'normal !important',
            wordSpacing: 'normal !important',
          }
        }}
      />
    );
  };

  const { permissions } = usePermissions();
  const TENANT_ADMIN_ROLE:string = getConfig('VITE_TENANT_ADMIN_ROLE');
  const tenantAdmin = permissions?.includes(TENANT_ADMIN_ROLE);
  console.log('isTenantAdmin', tenantAdmin);
  console.log('permissions', permissions);
  console.log('TENANT_ADMIN_ROLE', TENANT_ADMIN_ROLE);

  return (
    <div>
      {!hideWelcome && (
        <MenuItemLink to="/" primaryText="Welcome" leftIcon={<DashboardIcon />} onClick={onMenuClick} />
      )}

      {/* Tenant Management - MMAdmin specific */}
      {tenantAdmin && (
      <MenuItemLink
        key={"tenant"}
        to={`/tenant`}
        primaryText="Tenants"
        leftIcon={
          resources["tenant"]?.icon ? (
            React.createElement(resources["tenant"]?.icon)
          ) : (
            <CategoryIcon />
          )
        }
        onClick={onMenuClick}
      />
      )}

      {/* Application */}
      <MenuItemLink
        key={"application"}
        to={`/application`}
        primaryText={
          (resources["application"]?.options &&
            resources["application"]?.options?.label) ||
          "Applications"
        }
        leftIcon={
          resources["application"]?.icon ? (
            React.createElement(resources["application"]?.icon)
          ) : (
            <CategoryIcon />
          )
        }
        onClick={onMenuClick}
      />

      {/* AI Section */}
      <ListItem onClick={handleAIClick}>
        <ListItemIcon style={{ minWidth: "40px" }}>
          <ConfigIcon />
        </ListItemIcon>
        <ListItemText primary="AI" style={{ textAlign: "left" }} />
        {aiOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItem>

      <Collapse
        in={aiOpen}
        timeout="auto"
        unmountOnExit
        style={{ paddingLeft: "10px" }}
      >
        <Paper
          elevation={2}
          style={{
            width: "95%",
            marginLeft: "10px",
            borderRadius: "8px",
            maxHeight: "250px",
            overflowY: "auto",
          }}
        >
          <List component="div" disablePadding sx={{ letterSpacing: 'normal' }}>
            <SubMenuItem
              to="/ai/observatory"
              label="Observatory"
              Icon={VisibilityIcon}
              onClick={onMenuClick}
              padding={16}
              fontSize="0.875rem"
            />
            <ResourceSubMenuItem
              resourceName="aiassistant"
              onClick={onMenuClick}
              padding={16}
            />
            <ResourceSubMenuItem
              resourceName="aifileinstance"
              onClick={onMenuClick}
              padding={16}
            />
            <ResourceSubMenuItem
              resourceName="aifilestore"
              onClick={onMenuClick}
              padding={16}
            />
          </List>
        </Paper>
      </Collapse>

      {/* Dashboards */}
      <MenuItemLink
        to="/analytics-dashboards"
        primaryText="Dashboards"
        leftIcon={<AnalyticsIcon />}
        onClick={onMenuClick}
      />

      {/* Developer */}
      <MenuItemLink
        to="/developer"
        primaryText="Developer"
        leftIcon={<DeveloperIcon />}
        onClick={onMenuClick}
      />

      {/* Integration */}
      <MenuItemLink
        to="/compose"
        primaryText="Integration"
        leftIcon={<ComposeIcon />}
        onClick={onMenuClick}
      />

      {/* Data Section */}
      <ListItem onClick={handleDataClick}>
        <ListItemIcon style={{ minWidth: "40px" }}>
          <ConfigIcon />
        </ListItemIcon>
        <ListItemText primary="Data" style={{ textAlign: "left" }} />
        {dataOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItem>

      <Collapse
        in={dataOpen}
        timeout="auto"
        unmountOnExit
        style={{ paddingLeft: "10px" }}
      >
        <Paper
          elevation={2}
          style={{
            width: "95%",
            marginLeft: "10px",
            borderRadius: "8px",
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
          }}
        >
          <List component="div" disablePadding>
            {/* Data Models */}
            <SubMenuItem
              to="datamodel"
              label="Data Models"
              Icon={SchemaIcon}
              onClick={onMenuClick}
              fontSize="0.9rem"
            />
            <SubMenuItem
              to="schema"
              label="Schemas"
              Icon={SchemaIcon}
              onClick={onMenuClick}
              fontSize="0.9rem"
            />

            {/* Domain-grouped entities */}
            {!domainsLoading && domains.length > 0 && domains.map(domain => (
              <div key={`domain-${domain.id}`}>
                <ListItem 
                  onClick={() => toggleDomainSection(domain.id)}
                  sx={{ 
                    paddingLeft: '32px',
                    paddingY: '4px',
                    cursor: 'pointer',
                    borderLeft: expandedDomains.has(domain.id) ? '2px solid #2196f3' : '2px solid transparent',
                    backgroundColor: expandedDomains.has(domain.id) ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                    '&:hover': { 
                      backgroundColor: expandedDomains.has(domain.id) 
                        ? 'rgba(33, 150, 243, 0.12)' 
                        : 'rgba(255, 255, 255, 0.08)' 
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '32px' }}>
                    <DataIcon sx={{ fontSize: '18px' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={domain.name}
                    secondary={`${domain.schemas.length} entities`}
                    primaryTypographyProps={{ 
                      fontSize: '0.85rem',
                      fontWeight: 'medium',
                      color: expandedDomains.has(domain.id) ? '#2196f3' : 'inherit'
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.7rem',
                      color: 'rgba(255, 255, 255, 0.6)'
                    }}
                  />
                  {expandedDomains.has(domain.id) ? <ExpandLessIcon sx={{ fontSize: '16px' }} /> : <ExpandMoreIcon sx={{ fontSize: '16px' }} />}
                </ListItem>
                
                <Collapse in={expandedDomains.has(domain.id)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {domain.schemas
                      .sort((a, b) => (a.name || a.key || '').localeCompare(b.name || b.key || ''))
                      .map((schema, index) => (
                        <SubMenuItem
                          key={`${domain.id}-${schema.key}-${index}`}
                          to={schema.key}
                          label={schema.name || schema.key || 'Unnamed'}
                          Icon={SchemaIcon}
                          onClick={onMenuClick}
                          padding={52}
                          fontSize="0.75rem"
                        />
                      ))}
                  </List>
                </Collapse>
              </div>
            ))}

            {/* Standalone entities */}
            {!entitiesLoading && dynamicEntities.length > 0 && (() => {
              const domainEntityNames = new Set();
              domains.forEach(domain => {
                domain.schemas.forEach(schema => {
                  domainEntityNames.add(schema.key);
                });
              });

              const standaloneEntities = dynamicEntities.filter(
                entity => !domainEntityNames.has(entity.resourceName)
              );

              if (standaloneEntities.length === 0) return null;

              return (
                <>
                  {standaloneEntities.map(entity => (
                    <SubMenuItem
                      key={entity.resourceName}
                      to={entity.resourceName}
                      label={entity.label}
                      Icon={DataIcon}
                      onClick={onMenuClick}
                      padding={52}
                      fontSize="0.75rem"
                    />
                  ))}
                </>
              );
            })()}
          </List>
        </Paper>
      </Collapse>

      {/* Connections */}
      <MenuItemLink
        to="/connection"
        primaryText="Connections"
        leftIcon={<CableIcon />}
        onClick={onMenuClick}
      />

      {/* Authentication */}
      <MenuItemLink
        to="/authentication-provider"
        primaryText="Authentication"
        leftIcon={<SecurityIcon />}
        onClick={onMenuClick}
      />

      {logout}
    </div>
  );
};

export default MMAdminMenu;

/*
       {Object.keys(resources).filter(name => !excludedResources.includes(name)).map(name => (
                  <MenuItemLink
                      key={name}
                      to={`/${name}`}
                      primaryText={(resources[name].options && resources[name].options.label) || name}
                      leftIcon={resources[name].icon ? React.createElement(resources[name].icon) : <CategoryIcon />}
                      onClick={onMenuClick}
                  />
              ))}


               {/** 
      <MenuItemLink
        key={"tenant"}
        to={`/tenant`}
        primaryText={
          (resources["tenant"]?.options &&
            resources["tenant"]?.options?.label) ||
          "tenant"
        }
        leftIcon={
          resources["tenant"]?.icon ? (
            React.createElement(resources["tenant"]?.icon)
          ) : (
            <CategoryIcon />
          )
        }
        onClick={onMenuClick}
      />
      <MenuItemLink
        key={"brand"}
        to={`/brand`}
        primaryText={
          (resources["brand"]?.options && resources["brand"]?.options?.label) ||
          "brand"
        }
        leftIcon={
          resources["brand"]?.icon ? (
            React.createElement(resources["brand"]?.icon)
          ) : (
            <CategoryIcon />
          )
        }
        onClick={onMenuClick}
      />
                  */
